import { createClient } from '@supabase/supabase-js';
import repositoryConfig from '../../../../config.json';

const supabaseUrl = String(
  import.meta.env.VITE_SUPABASE_URL || repositoryConfig.SUPABASE_URL || '',
).trim();

// Capture password-recovery intent before Supabase consumes/normalizes the
// URL fragment. Recovery links create a temporary authenticated session by
// design; preserving this marker lets the app distinguish that session from a
// normal login even if the auth listener subscribes after URL processing.
function recoveryIntentFromCurrentUrl() {
  if (typeof window === 'undefined') return false;
  const marker = `${window.location.search || ''}&${window.location.hash || ''}`;
  return /(?:[?#&]|^)type=recovery(?:&|$)/i.test(marker);
}

export const initialPasswordRecoveryIntent = recoveryIntentFromCurrentUrl();

// Canonical browser authentication uses Supabase's current publishable-key
// contract. The repository value deliberately precedes the legacy VITE anon
// override so a stale deployment variable cannot shadow the correct project
// credential. The legacy anon key remains a temporary compatibility fallback.
const supabasePublishableKey = String(
  import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY
    || repositoryConfig.SUPABASE_PUBLISHABLE_KEY
    || import.meta.env.VITE_SUPABASE_ANON_KEY
    || repositoryConfig.SUPABASE_ANON_KEY
    || '',
).trim();

function legacyJwtMatchesProject(url, key) {
  if (!key || key.startsWith('sb_publishable_')) return true;
  const ref = String(url || '').match(/^https:\/\/([a-z0-9]+)\.supabase\.co/i)?.[1] || '';
  try {
    const payload = JSON.parse(atob(key.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return !ref || !payload?.ref || payload.ref === ref;
  } catch {
    return false;
  }
}

export const hasSupabaseConfig = Boolean(
  supabaseUrl
  && supabasePublishableKey
  && legacyJwtMatchesProject(supabaseUrl, supabasePublishableKey),
);

export const supabase = hasSupabaseConfig
  ? createClient(supabaseUrl, supabasePublishableKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
      },
    })
  : null;

// Parental approval must never reuse, refresh, overwrite, or persist the
// ordinary browser session. Only a new password authentication is accepted.
export function createParentalReauthClient() {
  if (!hasSupabaseConfig) throw new Error('WatchVIM account services are temporarily unavailable.');
  return createClient(supabaseUrl, supabasePublishableKey, {
    auth: {
      persistSession: false,
      autoRefreshToken: false,
      detectSessionInUrl: false,
      storageKey: 'watchvim-lg-parental-reauth',
    },
  });
}

